import {
  CloseOutlined,
  CloudDownloadOutlined,
  ExclamationCircleOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { Button, Modal } from "antd";
import { FC } from "react";
import "./index.css";

export interface TaskConflictSnapshot {
  title?: string;
  status?: string;
  priority?: string;
  assignees?: string[];
  due_time?: number;
  tags?: string[];
  content?: string;
}

interface TaskConflictModalProps {
  open: boolean;
  baseTask: TaskConflictSnapshot;
  serverTask: TaskConflictSnapshot;
  mine: TaskConflictSnapshot;
  memberName: (userId: string) => string;
  loading: boolean;
  onCancel: () => void;
  onUseServer: () => void;
  onConfirmMine: () => void;
}

const STATUS_LABELS: Record<string, string> = {
  todo: "待处理",
  doing: "进行中",
  blocked: "已阻塞",
  review: "待验收",
  done: "已完成",
  discarded: "已废弃",
};

const PRIORITY_LABELS: Record<string, string> = {
  low: "低优先级",
  medium: "中优先级",
  high: "高优先级",
  urgent: "紧急",
};

const FIELD_LABELS: Record<keyof TaskConflictSnapshot, string> = {
  title: "任务标题",
  status: "任务状态",
  priority: "优先级",
  assignees: "负责人",
  due_time: "预计完成时间",
  tags: "标签",
  content: "节点内容",
};

const FIELD_KEYS = Object.keys(FIELD_LABELS) as Array<keyof TaskConflictSnapshot>;

function normalizeValue(value: unknown) {
  return Array.isArray(value) ? [...value].sort() : value ?? null;
}

function isDifferent(left: unknown, right: unknown) {
  return JSON.stringify(normalizeValue(left)) !== JSON.stringify(normalizeValue(right));
}

function stripHtml(value: string) {
  return value
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const TaskConflictModal: FC<TaskConflictModalProps> = ({
  open,
  baseTask,
  serverTask,
  mine,
  memberName,
  loading,
  onCancel,
  onUseServer,
  onConfirmMine,
}) => {
  const conflictFields = FIELD_KEYS.filter(
    (key) =>
      isDifferent(baseTask[key], serverTask[key]) ||
      isDifferent(baseTask[key], mine[key])
  );

  const formatValue = (key: keyof TaskConflictSnapshot, value: unknown) => {
    if (key === "status") return STATUS_LABELS[String(value || "")] || "未设置";
    if (key === "priority") return PRIORITY_LABELS[String(value || "")] || "未设置";
    if (key === "assignees") {
      const assignees = Array.isArray(value) ? value : [];
      return assignees.length ? assignees.map(memberName).join("、") : "未设置";
    }
    if (key === "tags") {
      return Array.isArray(value) && value.length ? value.join("、") : "未设置";
    }
    if (key === "due_time") {
      const timestamp = Number(value);
      return timestamp
        ? new Intl.DateTimeFormat("zh-CN", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          }).format(new Date(timestamp))
        : "未设置";
    }
    if (key === "content") {
      const text = stripHtml(String(value || ""));
      return text || "暂无内容";
    }
    return String(value || "未设置");
  };

  return (
    <Modal
      maskClosable={false}
      keyboard={false}
      centered
      className="taskConflictModal"
      closable={false}
      destroyOnClose
      footer={null}
      onCancel={onCancel}
      open={open}
      title={null}
      width={980}
    >
      <div className="taskConflictModal__shell">
        <header className="taskConflictModal__header">
          <div className="taskConflictModal__icon"><ExclamationCircleOutlined /></div>
          <div>
            <span>协同编辑冲突</span>
            <h2>检测到并发修改</h2>
            <p>其他成员已先保存该任务，请比较差异后选择保留版本。</p>
          </div>
          <Button
            aria-label="关闭冲突弹窗"
            className="taskConflictModal__close"
            disabled={loading}
            icon={<CloseOutlined />}
            onClick={onCancel}
          />
        </header>

        <div className="taskConflictModal__columns" aria-hidden="true">
          <span>字段</span><span>我打开时</span><span>服务器最新</span><span>我的修改</span>
        </div>
        <div className="taskConflictModal__diffs">
          {conflictFields.map((key) => (
            <div className="taskConflictModal__row" key={key}>
              <strong>{FIELD_LABELS[key]}</strong>
              <span>{formatValue(key, baseTask[key])}</span>
              <span className="taskConflictModal__server">{formatValue(key, serverTask[key])}</span>
              <span className="taskConflictModal__mine">{formatValue(key, mine[key])}</span>
            </div>
          ))}
        </div>

        <footer className="taskConflictModal__footer">
          <Button disabled={loading} onClick={onCancel}>暂不处理</Button>
          <Button
            aria-label="载入服务器版本"
            className="taskConflictModal__useServer"
            disabled={loading}
            icon={<CloudDownloadOutlined />}
            onClick={onUseServer}
          >
            载入服务器版本
          </Button>
          <Button
            aria-label="确认使用我的修改"
            className="taskConflictModal__keepMine"
            icon={<SaveOutlined />}
            loading={loading}
            onClick={onConfirmMine}
          >
            使用我的修改
          </Button>
        </footer>
      </div>
    </Modal>
  );
};

export default TaskConflictModal;
